import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSizeChartsAccess } from '@/hooks/useSizeChartsAccess';
import { ROUTES } from '@/lib/constants/routes';
import { ROLES } from '@/lib/constants/app.constants';

/**
 * Protected route component for Size Charts pages.
 * Requires user to be authenticated AND have size charts access.
 * 
 * Access is granted if any of the following conditions are met:
 * 1. User email domain is 28one.com (isInternal)
 * 2. User is a system admin
 * 3. Tenant feature flag `enableSizechartsPage` is true
 */
export const SizeChartsProtectedRoute = () => {
  const { isAuthenticated, user } = useAuth();
  const { hasSizeChartsAccess } = useSizeChartsAccess();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Check if user has basic authorized role (planner or system admin)
  const hasAuthorizedRole = (): boolean => {
    if (!user || !user.roles || user.roles.length === 0) {
      return false;
    }
    return user.roles.includes(ROLES.PLANNER) || user.roles.includes(ROLES.SYSTEM_ADMIN);
  };

  if (!hasAuthorizedRole()) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // Check size charts specific access
  if (!hasSizeChartsAccess) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  return <Outlet />;
};
